import * as v from 'valibot';

/**
 * A recorded run, kept so an edited workflow can be replayed against it.
 *
 * This is the one capability reef is structurally positioned to do better than
 * anything else: config-in-code means a workflow is a reviewable document, and
 * a permanent audit trail means every past run is a recorded input with a
 * recorded outcome. Put the two together and you can replay last Tuesday's
 * incident against today's document and diff not just what the steps said but
 * which policy decisions they triggered.
 *
 * The recorded connector responses are what make it an eval rather than a live
 * test. Replaying a workflow that comments on a Linear issue must not comment
 * on a Linear issue.
 */
export const EvalRecording = v.object({
  id: v.string(),
  workflowId: v.string(),
  name: v.string(),
  triggerType: v.string(),
  triggerPayload: v.unknown(),
  /** Revision of the document the recording was made against. */
  revision: v.pipe(v.number(), v.integer()),
  recordedFromRun: v.nullable(v.string()),
  createdBy: v.nullable(v.string()),
  createdAt: v.string(),
});
export type EvalRecording = v.InferOutput<typeof EvalRecording>;

/**
 * What was observed during the recorded run, replayed in place of the world.
 */
export const EvalTape = v.object({
  /** Keyed by mount, tool and argument digest, so order does not matter. */
  calls: v.record(v.string(), v.unknown()),
  steps: v.array(
    v.object({
      stepId: v.string(),
      output: v.nullable(v.string()),
      toolCalls: v.array(v.string()),
      policyDecisions: v.array(v.string()),
      status: v.string(),
    }),
  ),
});
export type EvalTape = v.InferOutput<typeof EvalTape>;

export const EvalDiffEntry = v.object({
  stepId: v.string(),
  kind: v.picklist(['output', 'tool-calls', 'policy', 'status', 'missing', 'added']),
  expected: v.nullable(v.string()),
  actual: v.nullable(v.string()),
});
export type EvalDiffEntry = v.InferOutput<typeof EvalDiffEntry>;

export const EvalResult = v.object({
  id: v.string(),
  evalId: v.string(),
  revision: v.pipe(v.number(), v.integer()),
  passed: v.boolean(),
  diff: v.array(EvalDiffEntry),
  ranBy: v.nullable(v.string()),
  ranAt: v.string(),
});
export type EvalResult = v.InferOutput<typeof EvalResult>;

/**
 * Compare a replay against its recording.
 *
 * Policy decisions, tool calls and status are compared exactly; step *output*
 * is not. A model rewording the same conclusion is not a regression, and an
 * eval that fails on paraphrase is an eval somebody switches off within a week.
 * What is checked strictly is that the step reached the same state, called the
 * same things, and was allowed or refused the same way — which is what a
 * reviewer was actually agreeing to when they approved the document.
 */
export function diffTape(expected: EvalTape, actual: EvalTape): EvalDiffEntry[] {
  const diff: EvalDiffEntry[] = [];
  const remaining = new Map(actual.steps.map((step) => [step.stepId, step]));

  for (const step of expected.steps) {
    const observed = remaining.get(step.stepId);
    if (!observed) {
      diff.push({ stepId: step.stepId, kind: 'missing', expected: step.status, actual: null });
      continue;
    }
    remaining.delete(step.stepId);

    if (observed.status !== step.status) {
      diff.push({ stepId: step.stepId, kind: 'status', expected: step.status, actual: observed.status });
    }

    const expectedCalls = [...step.toolCalls].sort().join(', ');
    const actualCalls = [...observed.toolCalls].sort().join(', ');
    if (expectedCalls !== actualCalls) {
      diff.push({ stepId: step.stepId, kind: 'tool-calls', expected: expectedCalls, actual: actualCalls });
    }

    const expectedPolicy = [...step.policyDecisions].sort().join(', ');
    const actualPolicy = [...observed.policyDecisions].sort().join(', ');
    if (expectedPolicy !== actualPolicy) {
      diff.push({ stepId: step.stepId, kind: 'policy', expected: expectedPolicy, actual: actualPolicy });
    }
  }

  // A step the recording never saw. Usually the point of the edit, and always
  // worth showing rather than passing in silence.
  for (const [stepId, step] of remaining) {
    diff.push({ stepId, kind: 'added', expected: null, actual: step.status });
  }

  return diff;
}

/**
 * A stable key for one tool call, so a replay can find its recorded response.
 *
 * Arguments are canonicalised by sorting keys before hashing: the same call
 * written with its object literal in a different order is the same call, and an
 * eval that missed on key order would report a regression on a reformat.
 */
export function callKey(mount: string, tool: string, args: unknown): string {
  const canonical = canonicalise(args);
  let hash = 0;
  for (let index = 0; index < canonical.length; index++) {
    hash = (Math.imul(31, hash) + canonical.charCodeAt(index)) | 0;
  }
  return `${mount}.${tool}(${(hash >>> 0).toString(36)})`;
}

function canonicalise(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(canonicalise).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
    a < b ? -1 : a > b ? 1 : 0,
  );
  return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${canonicalise(entry)}`).join(',')}}`;
}
